import {
  Bracket,
  Seed,
  SeedItem,
  SeedTeam,
  IRenderSeedProps,
} from "react-brackets";
import { useState, useContext, useEffect } from "react";
import { InfoContext, Season, Match } from "../components/InfoContext";
import { PlatformContext } from "../components/Platform/PlatformContext";

// Define TypeScript interfaces
export interface Team {
  id: number | null;
  name: string | null;
  score?: number;
}

export interface Round {
  title: string;
  seeds: Match[];
}

interface TournamentBracketProps {
  mobileBreakpoint?: number;
  season: Season;
}

const CustomSeed = ({
  seed,
  breakpoint,
  roundIndex,
  seedIndex,
}: IRenderSeedProps) => {
  const homeTeam = seed.teams[0];
  const awayTeam = seed.teams[1];

  return (
    <Seed
      mobileBreakpoint={breakpoint}
      style={{ fontSize: 16, width: "100%", minWidth: 280 }}
    >
      <SeedItem style={{ width: "100%", text: "white", background: "#241f48" }}>
        <div>
          <SeedTeam
            style={{
              color:
                typeof homeTeam.score === "number" &&
                typeof awayTeam.score === "number" &&
                homeTeam.score >= awayTeam.score
                  ? "white"
                  : "grey",
              backgroundColor:
                typeof homeTeam.score === "number" &&
                typeof awayTeam.score === "number" &&
                homeTeam.score > awayTeam.score
                  ? "#615e7c"
                  : undefined,
            }}
          >
            <div>{homeTeam.name ? homeTeam.name : "----"}</div>
            <div>{homeTeam.score}</div>
          </SeedTeam>
          <SeedTeam
            style={{
              color:
                typeof homeTeam.score === "number" &&
                typeof awayTeam.score === "number" &&
                homeTeam.score <= awayTeam.score
                  ? "white"
                  : "grey",
              backgroundColor:
                typeof homeTeam.score === "number" &&
                typeof awayTeam.score === "number" &&
                homeTeam.score < awayTeam.score
                  ? "#615e7c"
                  : undefined,
            }}
          >
            <div>{awayTeam.name ? awayTeam.name : "----"}</div>
            <div>{awayTeam.score}</div>
          </SeedTeam>
        </div>
      </SeedItem>
      <div className="text-gray-400 font-light text-sm">{seed.date}</div>
    </Seed>
  );
};

function formatDate(dateStr: string, timeZone = "utc"): string {
  if (!dateStr) {
    return "";
  }
  const date = new Date(dateStr);
  const formattedDate = new Intl.DateTimeFormat("de-DE", {
    timeZone,
    day: "2-digit",
    month: "2-digit",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
    hour12: false,
  }).format(date);

  return formattedDate.replace(",", "");
}

function Extract_ko(matches: Match[]) {
  const { getTeamById } = useContext(InfoContext);
  const stageMapping: Record<string, string> = {
    QUARTERFINAL: "Quarter Final",
    SEMIFINAL: "Semi Final",
    FINAL: "Final",
    PLACE3: "3rd Place",
  };

  // Normalize match stage names while preserving order
  const normalizeStage = (stage: string) => {
    if (stage.startsWith("QUARTERFINAL")) return "QUARTERFINAL";
    if (stage.startsWith("SEMIFINAL")) return "SEMIFINAL";
    return stage;
  };

  // Sort matches to maintain correct order
  matches.sort((a, b) => {
    const stageA = normalizeStage(a.match_stage);
    const stageB = normalizeStage(b.match_stage);

    // Ensure QUARTERFINAL comes before SEMIFINAL
    const stageOrder = ["QUARTERFINAL", "SEMIFINAL", "FINAL", "PLACE3"];
    if (stageA !== stageB)
      return stageOrder.indexOf(stageA) - stageOrder.indexOf(stageB);

    // Within same stage, sort by match_stage to keep numerical order
    return a.match_stage.localeCompare(b.match_stage);
  });

  // Group matches into rounds
  const stageGroups: Record<string, Match[]> = {};
  matches.forEach((match) => {
    const normalizedStage = normalizeStage(match.match_stage);
    if (!stageGroups[normalizedStage]) {
      stageGroups[normalizedStage] = [];
    }
    stageGroups[normalizedStage].push(match);
  });

  // Convert grouped matches into rounds structure
  const rounds = Object.entries(stageMapping).map(([stageKey, stageTitle]) => ({
    title: stageTitle,
    seeds: (stageGroups[stageKey] || []).map((match) => ({
      id: match.id,
      date: formatDate(match.date),
      teams: [
        {
          id: match.home_id,
          name: match.home_id ? getTeamById(match.home_id)?.name : undefined,
          score: match.results.find((res) => res.is_final)?.score_home ?? "-",
        },
        {
          id: match.guest_id,
          name: match.guest_id ? getTeamById(match.guest_id)?.name : undefined,
          score: match.results.find((res) => res.is_final)?.score_guest ?? "-",
        },
      ],
    })),
  }));

  return rounds;
}

function useMediaQuery(query: string): boolean {
  const [matches, setMatches] = useState(false);

  useEffect(() => {
    const media = window.matchMedia(query);
    const listener = () => setMatches(media.matches);

    setMatches(media.matches);
    media.addEventListener("change", listener);
    return () => media.removeEventListener("change", listener);
  }, [query]);

  return matches;
}

export const TournamentBracket = ({
  mobileBreakpoint = 768,
  season,
}: TournamentBracketProps) => {
  const platform = useContext(PlatformContext);
  const isMobile = !platform?.isDesktop;
  const isSmallScreen = useMediaQuery(`(max-width: ${mobileBreakpoint}px)`);

  const [tabIndex, setTabIndex] = useState<number>(0);

  const handleTabIndexChange = (index: number) => {
    setTabIndex(index);
  };

  const handleSwipeChange = (index: number) => {
    setTabIndex(index);
  };

  if (!season) {
    return <div>Season data not available</div>;
  }

  const rounds = Extract_ko(season.matches);
  return (
    <div className="tournament-bracket flex-col justify-center mt-4 w-full sm:max-w-none">
      {isSmallScreen && rounds.length > 1 && (
        <div className="tabs relative flex w-64 bg-white/10 p-1 rounded-xl mx-auto mb-4">
          <div
            className={`absolute top-0 left-0 h-full w-1/4 bg-white/20 rounded-xl transition-transform duration-300 ease-in-out transform ${
              tabIndex === 0
                ? "translate-x-0"
                : tabIndex === 1
                ? "translate-x-full"
                : tabIndex === 2
                ? "translate-x-[200%]"
                : "translate-x-[300%]"
            }`}
          />
          {["QF", "SF", "F", "3P"].map((label, index) => (
            <button
              key={index}
              className={`relative z-10 flex-1 py-2 text-center font-medium ${
                tabIndex === index ? "text-white" : "text-gray-400"
              }`}
              onClick={() => handleTabIndexChange(index)}
            >
              {label}
            </button>
          ))}
        </div>
      )}
      <div className="justify-center">
        <Bracket
          rounds={rounds}
          renderSeedComponent={(props) => (
            <CustomSeed {...props} breakpoint={mobileBreakpoint} />
          )}
          swipeableProps={{
            enableMouseEvents: true,
            animateHeight: true,
            index: tabIndex,
            onChangeIndex: handleSwipeChange,
          }}
        />
      </div>
    </div>
  );
};
